import {
  type ButtonHTMLAttributes,
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactNode,
  useRef,
} from "react"
import { twMerge } from "tailwind-merge"
import { Icon, type IconProps } from "./Icon"

export type Color = "neutral" | "success" | "warning" | "danger"
export type Size = "xsmall" | "small" | "medium" | "large"
export type Variant = "default" | "solid" | "ghost" | "muted"

// Base button styles
const baseButtonStyles = twMerge(
  // .tt-button is just used as a global identifier
  "tt-button flex justify-center items-center font-medium relative whitespace-nowrap font-sans cursor-pointer",
  // Focus states - focus-visible
  "outline-none focus-visible:outline-sky-500 focus-visible:outline-[2px] focus-visible:outline-offset-2",
  // Disabled states
  "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
  // Nested buttons
  "[&_svg]:shrink-0",
)

// Size styles
const sizeStyles: Record<Size, string> = {
  xsmall: "gap-1 px-1.5 py-1 rounded-md text-xs h-tt-xsmall",
  small: "gap-1.5 px-2.5 py-1.5 rounded-lg text-sm h-tt-small",
  medium: "gap-2 px-3.5 py-2 rounded-lg text-sm h-tt-regular",
  large: "gap-3 px-5 py-3 rounded-lg text-sm h-tt-large",
}

// Variant and color combinations
const variantStyles: Record<Variant, Record<Color | "base", string>> = {
  solid: {
    base: "hover:opacity-90 shadow-[0_1px_2px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.3),inset_0_-1px_0_rgba(0,0,0,0.15)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.1),inset_0_-1px_0_rgba(0,0,0,0.2)]",
    neutral: twMerge("text-white bg-neutral-800", "dark:text-neutral-950 dark:bg-neutral-200"),
    success: twMerge("text-white bg-emerald-600", "dark:text-emerald-50 dark:bg-emerald-700"),
    warning: twMerge("text-white bg-yellow-600", "dark:text-yellow-100 dark:bg-yellow-700"),
    danger: twMerge("text-white bg-red-600", "dark:text-red-100 dark:bg-red-800"),
  },
  default: {
    base: "border border-solid border-border-default shadow-[0_1px_2px_rgba(0,0,0,0.025)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.1)]",
    neutral: twMerge(
      "text-fg-default bg-white border-border-default hover:bg-neutral-50",
      "dark:bg-[hsl(0,0%,8%)] dark:hover:bg-[hsl(0,0%,10%)]",
    ),
    success: twMerge(
      "text-emerald-700 bg-emerald-600/15 border-emerald-700/[0.09]",
      "dark:text-emerald-300 dark:bg-emerald-400/15 dark:border-emerald-300/[0.09]",
    ),
    warning: twMerge(
      "text-yellow-700 bg-yellow-600/15 border-yellow-700/[0.09]",
      "dark:text-yellow-300 dark:bg-yellow-400/15 dark:border-yellow-300/[0.09]",
    ),
    danger: twMerge(
      "text-red-800 bg-red-600/15 border-red-700/[0.09]",
      "dark:text-red-300 dark:bg-red-400/15 dark:border-red-300/[0.09]",
    ),
  },
  muted: {
    base: "border border-solid border-transparent hover:opacity-80",
    neutral: twMerge("text-fg-default bg-neutral-900/[0.04]", "dark:bg-neutral-300/[0.09]"),
    success: twMerge(
      "text-emerald-700 bg-neutral-900/[0.04]",
      "dark:text-emerald-300 dark:bg-neutral-300/[0.09]",
    ),
    warning: twMerge(
      "text-yellow-700 bg-neutral-900/[0.04]",
      "dark:text-yellow-300 dark:bg-neutral-300/[0.09]",
    ),
    danger: twMerge(
      "text-red-700 bg-neutral-900/[0.04]",
      "dark:text-red-300 dark:bg-neutral-300/[0.09]",
    ),
  },
  ghost: {
    base: "border border-solid border-transparent bg-transparent shadow-none dark:shadow-none",
    neutral: twMerge("text-fg-default hover:bg-neutral-950/5", "dark:hover:bg-neutral-300/10"),
    success: twMerge(
      "text-fg-emerald hover:bg-neutral-950/5",
      "dark:text-fg-emerald dark:hover:bg-neutral-300/10",
    ),
    warning: twMerge(
      "text-yellow-700 hover:bg-neutral-950/5",
      "dark:text-yellow-700 dark:hover:bg-neutral-300/10",
    ),
    danger: twMerge(
      "text-red-700 hover:bg-neutral-950/5",
      "dark:text-red-700 dark:hover:bg-neutral-300/10",
    ),
  },
}

export type ButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children" | "className"
> & {
  color?: Color
  className?: string
  /** The handler to call when the button is clicked. */
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"]
  /** Whether the button is loading. */
  loading?: boolean
  size?: Size
  /** The emphasis of the button. `solid` is the most prominent, `ghost` is
   * the least. `muted` should only be used for active button state patterns
   * like tabs. */
  variant?: Variant
  /** The content of the button. Can be a string or an icon. */
  children: ReactNode
}

export const iconSizeLookup = {
  xsmall: "12",
  small: "14",
  medium: "14",
  large: "14",
} as const

export const makeButtonClassName = (size: Size, variant: Variant, color: Color) => {
  return twMerge(
    baseButtonStyles,
    sizeStyles[size],
    variantStyles[variant].base,
    variantStyles[variant][color],
  )
}

/**
 * Trigger actions or events when clicked.
 *
 * ```tsx
 * import { Star } from "@phosphor-icons/react"
 * import { Button } from "@tokenterminal/core/ui/button/Button"
 *
 * <Button onClick={...}>Click me</Button>
 * <Button color="success" onClick={...}>
 *   <Icon as={Star} />
 *   Click me
 * </Button>
 * ```
 */

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const {
    children,
    className,
    color = "neutral",
    disabled,
    loading,
    size = "medium",
    variant = "default",
    ...rest
  } = props
  const fallbackRef = useRef<HTMLButtonElement>(null)
  const normalizedRef = ref || fallbackRef

  return (
    <button
      ref={normalizedRef}
      disabled={disabled ?? loading}
      className={twMerge(makeButtonClassName(size, variant, color), className)}
      {...rest}
    >
      {Children.map(children, (child) => {
        if (typeof child === "string") {
          return child
        } else if (isValidElement(child) && child.type === Icon) {
          return cloneElement(child, {
            width: iconSizeLookup[size as keyof typeof iconSizeLookup],
          } as Partial<IconProps>)
        }

        return child
      })}
    </button>
  )
})
